import type { DocumentSnapshot } from "firebase/firestore";
import { logger } from "../../analytics/logger";
import { Kaggle } from "../../sdk";
import { parseTimestamp } from "./common";

import AcceleratorType = Kaggle.Sdk.Kernels.AcceleratorType;

// Proto definition at: https://github.com/Kaggle/kaggleazure/blob/ci/Kaggle.Sdk/ActiveEvents/Protos/active_event.proto
export interface ActiveEvent {
  id: string;

  eventStart?: Date;
  eventEnd?: Date;
  stepStart?: Date;

  kernelSessionActiveEvent?: KernelSessionActiveEvent;
  scheduledKernelSessionActiveEvent?: ScheduledKernelSessionActiveEvent;
  datasetUploadActiveEvent?: DatasetUploadActiveEvent;
  competitionSubmissionActiveEvent?: CompetitionSubmissionActiveEvent;

  // Ids of the other events grouped under this event.
  groupedEventIds?: Array<string>;
}

export type WorkerStatus =
  | "unknown"
  | "queued"
  | "running"
  | "complete"
  | "error"
  | "cancelRequested"
  | "cancelAcknowledged"
  | "newScript";

export type KernelSessionExecutionType = "unknown" | "interactive" | "batch";

export interface KernelSessionActiveEvent {
  sessionId: number;
  kernelId: number;
  title: string;
  workerStatus: WorkerStatus;
  type: KernelSessionExecutionType;
  acceleratorType: AcceleratorType;
  versionNumber: number;
}

export interface ScheduledKernelSessionActiveEvent {
  scheduledKernelSessionId: number;
  kernelId: number;
  title: string;
  iteration: number;
  iterations: number;
  nextSessionDate: Date | undefined;
}

export type DatasetUploadStatus =
  | "creating"
  | "finished"
  | "failed"
  | "unknown";

export type DatasetUploadType = "creation" | "version" | "unknown";

export interface DatasetUploadActiveEvent {
  datasetId: number;
  datasetVersionId: number;
  title: string;
  url: string;
  thumbnailImage: string;
  versionNotes: string;
  percentDone: number;
  errorMessage: string;
  status: DatasetUploadStatus;
  type: DatasetUploadType;
}

export type SubmissionStatusType = "unknown" | "pending" | "complete" | "error";

export type SubmissionErrorType =
  | "unknown"
  | "submissionErrorTypeUnspecified"
  | "submissionFileNotFound"
  | "submissionLimitExceeded";

export interface CompetitionSubmissionActiveEvent {
  submissionId?: number;
  kernelSessionId?: number;
  url: string;
  competitionId: number;
  competitionTitle: string;
  competitionThumbnailImageUrl: string;
  status: SubmissionStatusType;
  errorType: SubmissionErrorType;
  errorDetails: string;
}

export const parseActiveEventFromDocument = (
  doc: DocumentSnapshot
): ActiveEvent | null => {
  const session = doc.get("kernelSession");
  const schedule = doc.get("scheduledKernelSession");
  const dataset = doc.get("datasetUpload");
  const submission = doc.get("competitionSubmission");
  if (session) {
    return {
      id: doc.id,
      eventStart: parseTimestamp(doc.get("eventStart")),
      eventEnd: parseTimestamp(doc.get("eventEnd")),
      stepStart: parseTimestamp(doc.get("stepStart")),
      kernelSessionActiveEvent: {
        sessionId: session.sessionId,
        kernelId: session.kernelId,
        title: session.title,
        workerStatus: parseWorkerStatus(session.workerStatus),
        type: parseKernelSessionExecutionType(session.type),
        acceleratorType: parseAcceleratorType(session.acceleratorType),
        versionNumber: session.versionNumber || 1,
      },
    };
  } else if (schedule) {
    return {
      id: doc.id,
      eventStart: parseTimestamp(doc.get("eventStart")),
      eventEnd: parseTimestamp(doc.get("eventEnd")),
      stepStart: parseTimestamp(doc.get("stepStart")),
      scheduledKernelSessionActiveEvent: {
        scheduledKernelSessionId: schedule.scheduledKernelSessionId,
        kernelId: schedule.kernelId,
        title: schedule.title,
        iteration: schedule.iteration,
        iterations: schedule.iterations,
        nextSessionDate: parseTimestamp(schedule.nextSessionDate),
      },
    };
  } else if (dataset) {
    return {
      id: doc.id,
      eventStart: parseTimestamp(doc.get("eventStart")),
      eventEnd: parseTimestamp(doc.get("eventEnd")),
      stepStart: parseTimestamp(doc.get("stepStart")),
      datasetUploadActiveEvent: {
        datasetId: dataset.datasetId,
        datasetVersionId: dataset.datasetVersionId,
        title: dataset.title,
        url: dataset.url,
        thumbnailImage: dataset.thumbnailImage,
        versionNotes: dataset.versionNotes,
        percentDone: dataset.percentDone,
        errorMessage: dataset.errorMessage,
        status: parseDatasetUploadStatus(dataset.status),
        type: parseDatasetUploadType(dataset.type),
      },
    };
  } else if (submission) {
    return {
      id: doc.id,
      eventStart: parseTimestamp(doc.get("eventStart")),
      eventEnd: parseTimestamp(doc.get("eventEnd")),
      stepStart: parseTimestamp(doc.get("stepStart")),
      competitionSubmissionActiveEvent: {
        submissionId: submission.submissionId,
        kernelSessionId: submission.kernelSessionId,
        url: submission.url,
        competitionId: submission.competitionId,
        competitionTitle: submission.competitionTitle,
        competitionThumbnailImageUrl: submission.competitionThumbnailImageUrl,
        status: parseCompetitionSubmissionStatus(submission.status),
        errorType: parseCompetitionSubmissionErrorType(submission.errorType),
        errorDetails: submission.errorDetails,
      },
    };
  }

  logger.report("navigation", "active-events-parse-unknown");
  return null;
};

const parseWorkerStatus = (status: any): WorkerStatus => {
  // C# Enum: https://github.com/Kaggle/kaggleazure/blob/ci/Kaggle.Sdk/Kernels/Models/KernelWorkerStatus.cs
  switch (status) {
    case 0:
      return "queued";
    case 1:
      return "running";
    case 2:
      return "complete";
    case 3:
      return "error";
    case 4:
      return "cancelRequested";
    case 5:
      return "cancelAcknowledged";
    default:
      return "unknown";
  }
};

const parseKernelSessionExecutionType = (
  status: any
): KernelSessionExecutionType => {
  // Proto enum: https://github.com/Kaggle/kaggleazure/blob/ci/Kaggle.Sdk/ActiveEvents/Protos/active_event.proto
  switch (status) {
    case 1:
      return "interactive";
    case 2:
      return "batch";
    default:
      return "unknown";
  }
};

const parseAcceleratorType = (status: any): AcceleratorType => {
  switch (status) {
    case 2:
      return AcceleratorType.GPU;
    case 4:
      return AcceleratorType.TPU_V3_8;
    case 5:
      return AcceleratorType.TPU_V2_32;
    case 7:
      return AcceleratorType.TPU_V2_256;
    default:
      return AcceleratorType.NONE;
  }
};

const parseDatasetUploadType = (status: any): DatasetUploadType => {
  // Proto enum: https://github.com/Kaggle/kaggleazure/blob/ci/Kaggle.Sdk/ActiveEvents/Protos/active_event.proto
  switch (status) {
    case 1:
      return "creation";
    case 2:
      return "version";
    default:
      return "unknown";
  }
};

const parseDatasetUploadStatus = (status: any): DatasetUploadStatus => {
  // Proto enum: https://github.com/Kaggle/kaggleazure/blob/ci/Kaggle.Sdk/ActiveEvents/Protos/active_event.proto
  switch (status) {
    case 1:
      return "creating";
    case 2:
      return "finished";
    case 3:
      return "failed";
    default:
      return "unknown";
  }
};

const parseCompetitionSubmissionStatus = (
  status: any
): SubmissionStatusType => {
  // Proto enum: https://github.com/Kaggle/kaggleazure/blob/ci/Kaggle.Sdk/ActiveEvents/Protos/active_event.proto
  switch (status) {
    case 0:
      return "pending";
    case 1:
      return "complete";
    case 2:
      return "error";
    default:
      return "unknown";
  }
};

const parseCompetitionSubmissionErrorType = (
  errorType: any
): SubmissionErrorType => {
  // Proto enum: https://github.com/Kaggle/kaggleazure/blob/ci/Kaggle.Sdk/ActiveEvents/Protos/active_event.proto
  switch (errorType) {
    case 0:
      return "submissionErrorTypeUnspecified";
    case 1:
      return "submissionFileNotFound";
    case 2:
      return "submissionLimitExceeded";
    default:
      return "unknown";
  }
};
